import { cachedStepDecode, fitConversationPrompt } from "./byte-decode.js";
import { dslDiagnostics, extractDsl } from "./crown-dsl.js";
import * as __rspack_external_onnxruntime_web_webgpu_363cf895 from "onnxruntime-web/webgpu";
const SCENARIO = 'crown-dsl';
const MAX_NEW = 160;
const DEFAULT_MODEL_CACHE = 'crown-dsl-model-v1';
let ortConfigured = false;
function configureOrt(wasmPaths) {
    if (ortConfigured) return;
    __rspack_external_onnxruntime_web_webgpu_363cf895.env.wasm.wasmPaths = wasmPaths;
    const isolated = "u" > typeof crossOriginIsolated && crossOriginIsolated;
    __rspack_external_onnxruntime_web_webgpu_363cf895.env.wasm.numThreads = isolated ? Math.min(4, navigator.hardwareConcurrency || 1) : 1;
    __rspack_external_onnxruntime_web_webgpu_363cf895.env.wasm.simd = true;
    __rspack_external_onnxruntime_web_webgpu_363cf895.env.logLevel = 'error';
    ortConfigured = true;
}
async function fetchModelBytes(modelUrl, cacheName, onProgress, signal) {
    const cache = "u" > typeof caches ? await caches.open(cacheName).catch((cause)=>{
        console.warn('model cache unavailable, fetching over the network', cause);
        return null;
    }) : null;
    let response = cache ? await cache.match(modelUrl).catch((cause)=>{
        console.warn('model cache lookup failed, fetching over the network', cause);
    }) : void 0;
    const fromCache = null != response;
    if (!response) {
        response = await fetch(modelUrl, {
            signal
        });
        if (!response.ok) throw new Error(`model fetch failed (${response.status})`);
        if (cache) await cache.put(modelUrl, response.clone()).catch((cause)=>{
            console.warn('caching the model for next time failed', cause);
        });
    }
    const total = Number(response.headers.get('Content-Length')) || 0;
    if (!response.body) {
        const buf = new Uint8Array(await response.arrayBuffer());
        onProgress?.({
            loaded: buf.length,
            total: total || buf.length,
            fromCache
        });
        return buf;
    }
    const reader = response.body.getReader();
    const chunks = [];
    let loaded = 0;
    for(;;){
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        loaded += value.length;
        onProgress?.({
            loaded,
            total,
            fromCache
        });
    }
    const bytes = new Uint8Array(loaded);
    let offset = 0;
    for (const chunk of chunks){
        bytes.set(chunk, offset);
        offset += chunk.length;
    }
    return bytes;
}
function webStepSession(sess) {
    const meta = sess.inputMetadata;
    return {
        inputNames: sess.inputNames,
        inputShape: (name)=>meta.find((m)=>m.name === name)?.shape,
        run: (feeds)=>sess.run(feeds),
        int64: (data, dims)=>new __rspack_external_onnxruntime_web_webgpu_363cf895.Tensor('int64', data, dims),
        float32: (data, dims)=>new __rspack_external_onnxruntime_web_webgpu_363cf895.Tensor('float32', data, dims)
    };
}
async function createSession(bytes, preferWebGpu) {
    const create = (eps)=>__rspack_external_onnxruntime_web_webgpu_363cf895.InferenceSession.create(bytes, {
            executionProviders: eps,
            graphOptimizationLevel: 'all'
        });
    const gpuAvailable = preferWebGpu && "u" > typeof navigator && 'gpu' in navigator && Boolean(navigator.gpu);
    if (gpuAvailable) try {
        const session = await create([
            'webgpu'
        ]);
        await warmup(session);
        return {
            session,
            backend: 'webgpu'
        };
    } catch  {}
    const session = await create([
        'wasm'
    ]);
    return {
        session,
        backend: 'wasm'
    };
}
async function warmup(session) {
    const { prompt, maxNew } = fitConversationPrompt(SCENARIO, [
        'ok'
    ], 1);
    await cachedStepDecode(webStepSession(session), prompt, Math.min(1, maxNew));
}
async function loadDslEngine(assets, opts = {}) {
    configureOrt(assets.wasmPaths);
    const bytes = await fetchModelBytes(assets.modelUrl, assets.cacheName ?? DEFAULT_MODEL_CACHE, opts.onProgress, opts.signal);
    const { session, backend } = await createSession(bytes, opts.preferWebGpu ?? true);
    const adapter = webStepSession(session);
    let chain = Promise.resolve();
    const runExclusive = (task)=>{
        const next = chain.then(task, task);
        chain = next.catch((cause)=>cause);
        return next;
    };
    const decode = async (turns)=>{
        const { prompt, maxNew } = fitConversationPrompt(SCENARIO, turns, MAX_NEW);
        const outBytes = await cachedStepDecode(adapter, prompt, maxNew);
        return new TextDecoder().decode(new Uint8Array(outBytes)).trim();
    };
    return {
        backend,
        generate: (description, generateOpts = {})=>runExclusive(async ()=>{
                const raw = await decode([
                    description
                ]);
                let dsl = extractDsl(raw) ?? '';
                let error = dslDiagnostics(dsl);
                let repaired = false;
                if (null != error && generateOpts.repair) {
                    const rawRepair = await decode([
                        description,
                        dsl,
                        error
                    ]);
                    const repairedDsl = extractDsl(rawRepair) ?? '';
                    const repairError = dslDiagnostics(repairedDsl);
                    if (null == repairError && repairedDsl) {
                        dsl = repairedDsl;
                        error = null;
                        repaired = true;
                    }
                }
                return {
                    dsl,
                    valid: null == error,
                    diagnostics: error,
                    repaired
                };
            })
    };
}
export { loadDslEngine };
